import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import { useThemeMode } from '../../utils/useThemeMode';

import { Link } from 'react-router-dom';

const JobTile = ({ job, idx, selectUpdateJob, deleteJobThunk }) => {
  const { mode } = useThemeMode();
  
  // React logo blue color for accents
  const reactBlue = 'rgba(97, 218, 251, 0.9)';
  // Google's classic pin red
  const googleRed = '#EA4335';

  const cardStyles = {
    width: '230px',
    position: 'relative',
    padding: '5px',
    height: 'auto',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    border: mode === 'dark' ? `1px solid ${reactBlue}` : '1px solid #e0e0e0',
    boxShadow: mode === 'dark' ? `0 2px 8px rgba(97, 218, 251, 0.15)` : undefined,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: mode === 'dark' 
        ? `0 4px 12px rgba(97, 218, 251, 0.25)` 
        : '0 4px 10px rgba(0, 0, 0, 0.1)',
    }
  };

  const cardHeadingStyle = {
    color: mode === 'dark' ? reactBlue : '#1976d2',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '8px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.2',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  };

  const companyStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    marginBottom: '8px',
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : undefined
  };

  const locationStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
  };

  const deleteButtonStyle = {
    position: 'absolute',
    top: '5px',
    right: '5px',
    zIndex: 10,
    color: mode === 'dark' ? reactBlue : undefined
  };

  const iconStyle = {
    fontSize: '1rem',
    marginRight: '4px',
    color: mode === 'dark' ? reactBlue : undefined
  };

  // Special styling for the location icon - Google red in light mode
  const locationIconStyle = {
    fontSize: '1rem',
    marginRight: '4px',
    color: mode === 'dark' ? reactBlue : googleRed,
  };

  const cardActionsStyle = {
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto',
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Get a preview of notes (first note or empty string)
  const notePreview = Array.isArray(job.note) && job.note.length > 0 
    ? truncateText(job.note[0], 60) 
    : '';

  return (
    <Draggable draggableId={job.id} index={idx}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Card
            progress={job.progress_stage}
            className="mb-2 mx-auto"
            sx={cardStyles}
            variant="outlined"
          >
            <IconButton
              sx={deleteButtonStyle}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteJobThunk();
              }}
              size="small"
            >
              <i className="fas fa-times"></i>
            </IconButton>
            
            <CardContent sx={{ 
              padding: '16px 8px 8px 8px', 
              flex: '1 0 auto',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : undefined,
              backgroundColor: mode === 'dark' ? 'rgba(10, 25, 41, 0.8)' : undefined
            }}>
              {/* Job Title - Main focus, always clickable if URL exists */}
              <Typography
                variant="h6"
                component="div"
                sx={cardHeadingStyle}
              >
                {job.url ? (
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                    className="hover-underline"
                  >
                    {job.title}
                  </a>
                ) : (
                  job.title
                )}
              </Typography>
              
              {/* Company with icon */}
              <Typography sx={companyStyle} variant="body2">
                <BusinessIcon sx={iconStyle} />
                {truncateText(job.company_name, 28)}
              </Typography>
              
              {/* Location with icon - using locationIconStyle for the icon */}
              <Typography sx={locationStyle} variant="body2">
                <LocationOnIcon sx={locationIconStyle} />
                {truncateText(job.location, 28)}
              </Typography>
              
              {/* Only show note preview if it exists */}
              {notePreview && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    marginTop: '8px',
                    fontSize: '0.75rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontStyle: 'italic',
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                  }}
                >
                  "{notePreview}"
                </Typography>
              )}
            </CardContent>
            
            <CardActions sx={{
              ...cardActionsStyle,
              backgroundColor: mode === 'dark' ? 'rgba(10, 25, 41, 0.8)' : undefined
            }}>
              <Link to={`/updatejob?id=${job.id}`}>
                <Tooltip title="Edit this job" placement="left">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ 
                      minWidth: '32px', 
                      padding: '4px',
                      backgroundColor: mode === 'dark' ? reactBlue : undefined,
                      '&:hover': {
                        backgroundColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.7)' : undefined
                      }
                    }}
                  >
                    <i className="fas fa-pen"></i>
                  </Button>
                </Tooltip>
              </Link>
            </CardActions>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

export default JobTile;
